import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { resolveUrl, API_PREFIX } from 'src/core/constants/api.constants';
import { SipCredentialsDto } from '../telephony/models/sip-credentials.model';
import { SessionSummaryDto } from '../components/copilot-panel/models/session-summary.model';
import { BreakTodayItemDto } from '../components/copilot-panel/models/break-today-item.model';
import { WorkTimeTodayDto } from '../components/copilot-panel/models/work-time-today.model';
import { TranscriptMessageDto } from '../components/copilot-panel/models/transcript-message-dto.model';
import { AiInsightsDto } from '../components/copilot-panel/models/ai-insights.dto';
import { DeclineReasonItemDto } from 'src/core/models/decline-reason/decline-reason-item.dto';
import { QueueItemDto } from 'src/core/models/queue/queue-item.dto';
import { DialerSearchItemDto } from '../telephony/models/dialer-search-item.model';
import { DialerSearchItemType } from '../telephony/models/dialer-search-item-type.enum';
import { DefaultOutboundNumberDto } from '../telephony/models/default-outbound-number.model';
import { RecordSegmentEventInput } from '../telephony/models/record-segment-event-input.model';

const AGENT_DESKTOP = `${API_PREFIX}/agent-desktop`;

const AGENT_DESKTOP_ROUTE = '/agent-desktop';

export const AGENT_DESKTOP_DIAL_PARAM = 'dial';

export const AGENT_DESKTOP_DIAL_TYPE_PARAM = 'dialType';

export type AgentDesktopDialType = 'internal' | 'outbound';

@Injectable({ providedIn: 'root' })
export class AgentDesktopService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private defaultOutboundNumber$?: Observable<DefaultOutboundNumberDto | null>;

  private assignedQueues$?: Observable<QueueItemDto[]>;

  getSipCredentials(): Observable<SipCredentialsDto> {
    return this.http
      .get<SipCredentialsDto>(
        resolveUrl(`${AGENT_DESKTOP}/sip-credentials`)
      )
      .pipe(
        map(dto => this.normalizeCredentials(dto)),
        catchError(error => this.handleError('getSipCredentials', error)),
      );
  }

  /**
   * Spreads the response and only overrides what actually needs normalizing. Do not re-expand this
   * into a field-by-field copy: every optional field on SipCredentialsDto would then have to be
   * repeated here, and forgetting one silently drops it with no type error (this is exactly how
   * `extension` went missing and made the UI show the SIP username instead of the extension).
   */
  private normalizeCredentials(dto: SipCredentialsDto): SipCredentialsDto {
    return {
      ...dto,
      username: dto.username ?? '',
    };
  }

  getSessionSummary(sessionId: number): Observable<SessionSummaryDto> {
    return this.http
      .get<SessionSummaryDto>(
        resolveUrl(`${AGENT_DESKTOP}/session-summary/${sessionId}`)
      )
      .pipe(
        catchError(error =>
          this.handleError('getSessionSummary', error)
        ),
      );
  }

  getBreakToday(sessionId: number): Observable<BreakTodayItemDto[]> {
    return this.http
      .get<BreakTodayItemDto[]>(
        resolveUrl(`${AGENT_DESKTOP}/break-today/${sessionId}`)
      )
      .pipe(
        catchError(error =>
          this.handleError('getBreakToday', error)
        ),
      );
  }

  getWorkTimeToday(sessionId: number): Observable<WorkTimeTodayDto> {
    return this.http
      .get<WorkTimeTodayDto>(
        resolveUrl(`${AGENT_DESKTOP}/work-time-today/${sessionId}`)
      )
      .pipe(
        catchError(error =>
          this.handleError('getWorkTimeToday', error)
        ),
      );
  }

  rejectSegment(declineReasonId: number): Observable<void> {
    return this.http
      .post<void>(
        resolveUrl(`${AGENT_DESKTOP}/reject-segment`),
        { rejectReason: declineReasonId },
      )
      .pipe(
        catchError(error => this.handleError('rejectSegment', error)),
      );
  }

  recordSegmentHoldMuteEvent(input: RecordSegmentEventInput): Observable<void> {
    return this.http
      .post<void>(
        resolveUrl(`${AGENT_DESKTOP}/record-segment-hold-mute-event`),
        input,
      )
      .pipe(
        catchError(error => this.handleError('recordSegmentHoldMuteEvent', error)),
      );
  }

  transferCall(input: {
    targetAgentId?: string;
    targetQueueId?: number;
  }): Observable<void> {
    return this.http
      .post<void>(
        resolveUrl(`${AGENT_DESKTOP}/transfer-call`),
        input,
      )
      .pipe(
        catchError(error => this.handleError('transferCall', error)),
      );
  }

  /**
   * Fetch the AI-phase transcript of the active call. `conversationUid` is the value the INVITE
   * carried in its `X-CX-Conversation-Uid` header, so it is known while the call still rings.
   */
  getConversationTranscript(
    conversationUid: string,
  ): Observable<{ messages: TranscriptMessageDto[] }> {
    return this.http
      .get<{ messages: TranscriptMessageDto[] }>(
        resolveUrl(`${AGENT_DESKTOP}/conversation-transcript`),
        { params: { conversationUid } },
      )
      .pipe(
        catchError(error =>
          this.handleError('getConversationTranscript', error),
        ),
      );
  }

  /**
   * Fetch AI-generated insights (summary, topic, sentiment, risk indicators) for the
   * active call's conversation. The request is sent with the `hide-errors` header so a
   * backend failure never raises a global toast; on error the observable resolves to an
   * empty insights object, letting the UI degrade to blank fields gracefully.
   * Mirrors {@link getConversationTranscript}.
   */
  getAiInsights(conversationUid: string): Observable<AiInsightsDto> {
    return this.http
      .get<AiInsightsDto>(
        resolveUrl(`${AGENT_DESKTOP}/ai-insights`),
        {
          headers: new HttpHeaders({ 'hide-errors': 'true' }),
          params: { conversationUid },
        },
      )
      .pipe(catchError(() => of(this.createEmptyAiInsights())));
  }

  private createEmptyAiInsights(): AiInsightsDto {
    return {
      summary: '',
      topic: '',
      averageSentiment: 0,
      churnRisk: '',
      nonResolutionRisk: '',
      conversationResults: '',
      actions: '',
      improvementAreas: '',
    };
  }

  /**
   * Fetch the configured decline reasons.
   * @param suppressErrors When true, the request is sent with the `hide-errors`
   * header (global toast suppressed) and failures resolve to an empty list
   * instead of propagating. Used by background lookups (e.g. conversation list)
   * that should degrade silently; defaults to false to preserve the existing
   * error-surfacing behaviour for the reject flow.
   * @returns Observable emitting the decline reason items.
   */
  getDeclineReasons(suppressErrors = false): Observable<DeclineReasonItemDto[]> {
    const options = suppressErrors
      ? { headers: new HttpHeaders({ 'hide-errors': 'true' }) }
      : undefined;

    return this.http
      .get<DeclineReasonItemDto[]>(
        resolveUrl(`${AGENT_DESKTOP}/decline-reasons`),
        options,
      )
      .pipe(
        catchError(error =>
          suppressErrors
            ? of([])
            : this.handleError('getDeclineReasons', error),
        ),
      );
  }

  getAssignedQueues(): Observable<QueueItemDto[]> {
    if (!this.assignedQueues$) {
      this.assignedQueues$ = this.http
        .get<QueueItemDto[]>(resolveUrl(`${AGENT_DESKTOP}/assigned-queues`), {
          headers: new HttpHeaders({ 'hide-errors': 'true' }),
        })
        .pipe(
          map(queues => queues ?? []),
          catchError(() => of([] as QueueItemDto[])),
          shareReplay(1),
        );
    }

    return this.assignedQueues$;
  }

  getDefaultOutboundNumber(): Observable<DefaultOutboundNumberDto | null> {
    if (!this.defaultOutboundNumber$) {
      this.defaultOutboundNumber$ = this.http
        .get<DefaultOutboundNumberDto | null>(
          resolveUrl(`${AGENT_DESKTOP}/default-outbound-number`),
          { headers: new HttpHeaders({ 'hide-errors': 'true' }) },
        )
        .pipe(
          map(dto => (dto?.phoneNumber ? dto : null)),
          catchError(() => of(null)),
          shareReplay(1),
        );
    }

    return this.defaultOutboundNumber$;
  }

  getDialerSearch(
    query: string,
    category: DialerSearchItemType,
    skipCount = 0,
    maxResultCount = 10,
  ): Observable<{ totalCount: number; items: DialerSearchItemDto[] }> {
    return this.http
      .get<{ totalCount: number; items: DialerSearchItemDto[] }>(
        resolveUrl(`${AGENT_DESKTOP}/dialer-search`),
        {
          params: {
            query,
            category: category.toString(),
            skipCount: skipCount.toString(),
            maxResultCount: maxResultCount.toString(),
          },
        },
      )
      .pipe(catchError(error => this.handleError('getDialerSearch', error)));
  }

  launchCall(destination: string, dialType: AgentDesktopDialType): Promise<boolean> {
    const trimmed = destination?.trim();
    if (!trimmed) {
      return Promise.resolve(false);
    }

    return this.router.navigate([AGENT_DESKTOP_ROUTE], {
      queryParams: {
        [AGENT_DESKTOP_DIAL_PARAM]: trimmed,
        [AGENT_DESKTOP_DIAL_TYPE_PARAM]: dialType,
      },
    });
  }

  private handleError(context: string, error: unknown): Observable<never> {
    console.error(`AgentDesktopService.${context} error:`, error);
    return throwError(() => error);
  }
}
